import { ReactElement, useCallback, useMemo } from 'react';
import { Menu, Button, Divider } from '@mui/material';
import { compact } from 'lodash';

import { OrdersFiltersPresetsV2 } from '~components/index';
import { Nullable } from '~globals/types/commons';
import { ItemPresetParse } from '~services/item/types';

import { OrdersFiltersMenuV2Props, OrdersFiltersMenuV2Type } from './types';
import OrdersFiltersMenuV2Provider from './Context';
import OrdersFiltersMenuV2Variant from './Variant';
import {
  OrdersFiltersMenuV2Content,
  OrdersFiltersMenuV2Actions,
  OrdersFiltersMenuV2ActionsButton,
} from './styles';

const OrdersFiltersMenuV2 = ({
  anchorEl,
  anchorOrigin = { horizontal: 'center', vertical: 'bottom' },
  transformOrigin = { horizontal: 'center', vertical: 'top' },
  open,
  onClose,
  type,
  filters,
  selectsData,
  presets,
}: OrdersFiltersMenuV2Props): ReactElement => {
  const handleManualClose = useCallback(() => {
    onClose?.({}, 'backdropClick');
  }, [onClose]);

  const selectedFilter = useMemo(() => {
    if (type === 'finalized') return filters.finalized;

    if (type === 'inTransit') return filters.inTransit;

    if (type === 'collect') return filters.collect;

    return filters.inWarehouse;
  }, [filters, type]);

  const handleApplyFilter = useCallback(() => {
    selectedFilter.onApplyFilters();
    handleManualClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter.onApplyFilters, handleManualClose]);

  const handleClearFilters = useCallback(() => {
    selectedFilter.onClearFilters();
    presets?.selectedPreset.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter.onClearFilters, presets?.selectedPreset]);

  const handleClose = useCallback(() => {
    handleManualClose();

    selectedFilter.onResetFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleManualClose, selectedFilter.onResetFilters]);

  const isValidToApply = useMemo(
    () =>
      !(
        JSON.stringify(selectedFilter.filters) ===
        JSON.stringify(selectedFilter.activeFilters)
      ),
    [selectedFilter.filters, selectedFilter.activeFilters],
  );

  const isEmptyFilters = useMemo(() => {
    const currentFilters = [
      // COMMONS
      selectedFilter.filters.search,
      selectedFilter.filters.expirationDateTimeFrom,
      selectedFilter.filters.expirationDateTimeTo,
      selectedFilter.filters.includeWithoutExpirationDate?.toString(),
      selectedFilter.filters.dateFrom,
      selectedFilter.filters.dateTo,
      // SPECIFIC ITEMS
      selectedFilter.filters.finalizedDateTimeFrom,
      selectedFilter.filters.finalizedDateTimeTo,
      selectedFilter.filters.unit1Gt,
      selectedFilter.filters.unit1Lt,
      selectedFilter.filters.unit2Gt,
      selectedFilter.filters.unit2Lt,
      selectedFilter.filters.priceGt,
      selectedFilter.filters.priceLt,
      selectedFilter.filters.vehicles?.length,
      selectedFilter.filters.drivers?.length,
      selectedFilter.filters.services?.length,
      selectedFilter.filters.zones?.length,
      selectedFilter.filters.assignedUser,
      selectedFilter.filters.sourceImports?.length,
      selectedFilter.filters.itemState?.toString(),
      // SPECIFIC COLLECT
      selectedFilter.filters.giverCompany,
      selectedFilter.filters.driver,
      selectedFilter.filters.collectState?.toString(),
    ];

    const compactCurrentFilters = compact(currentFilters);

    return compactCurrentFilters.length === 0;
  }, [selectedFilter.filters]);

  const handleChangePreset = useCallback(
    (currentPreset: Nullable<ItemPresetParse>) => {
      if (presets) {
        presets.selectedPreset.handleChange(currentPreset);

        if (currentPreset) {
          handleManualClose();
        }
      }
    },
    [handleManualClose, presets],
  );

  const handleGenericSuccessPresetDialog = useCallback(
    (currentPreset: ItemPresetParse) => {
      if (presets) {
        handleManualClose();
        presets.handleApplyFiltersByPreset(currentPreset);
        presets.reload();
      }
    },
    [handleManualClose, presets],
  );

  const handleConfirDeletePresetDialog = useCallback(() => {
    if (presets) {
      presets.selectedPreset.reset();
      presets.reload();
    }
  }, [presets]);

  const gruoupedPresetsByFilterTabId = useMemo(
    () =>
      presets.data.reduce(
        (acc, preset) => {
          const tabId = preset.data.tabId as Nullable<OrdersFiltersMenuV2Type>;

          if (tabId) {
            if (!acc[tabId]) {
              acc[tabId] = [];
            }
            acc[tabId].push(preset);
          }

          return acc;
        },
        {
          collect: [],
          inWarehouse: [],
          inTransit: [],
          finalized: [],
        } as Record<OrdersFiltersMenuV2Type, ItemPresetParse[]>,
      ),
    [presets.data],
  );

  const currentPresentsDataByFilterTabId = useMemo(
    () => gruoupedPresetsByFilterTabId[type],
    [type, gruoupedPresetsByFilterTabId],
  );

  return (
    <Menu
      id="orders-filters-menu-v2"
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      open={open}
      onClose={handleClose}
      keepMounted
    >
      <OrdersFiltersMenuV2Provider value={{ type, filters, selectsData }}>
        <OrdersFiltersMenuV2Content>
          <OrdersFiltersMenuV2Variant type={type} />

          <OrdersFiltersMenuV2Actions>
            <OrdersFiltersMenuV2ActionsButton>
              <Button
                variant="contained"
                color="primary"
                onClick={handleApplyFilter}
                disabled={!isValidToApply}
                fullWidth
              >
                Aplicar
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearFilters}
                disabled={isEmptyFilters}
                fullWidth
              >
                Borrar Filtros
              </Button>
            </OrdersFiltersMenuV2ActionsButton>

            <Divider orientation="vertical" flexItem />

            <OrdersFiltersPresetsV2
              options={currentPresentsDataByFilterTabId}
              loading={presets.loading}
              filterTabId={type}
              currentFilters={selectedFilter.filters}
              selectedPreset={presets.selectedPreset.value}
              onChangePreset={handleChangePreset}
              actionsDialogs={{
                create: { onSuccess: handleGenericSuccessPresetDialog },
                confirmEdit: { onSuccess: handleGenericSuccessPresetDialog },
                confirmDelete: { onSuccess: handleConfirDeletePresetDialog },
              }}
            />
          </OrdersFiltersMenuV2Actions>
        </OrdersFiltersMenuV2Content>
      </OrdersFiltersMenuV2Provider>
    </Menu>
  );
};

export default OrdersFiltersMenuV2;
